/**
 * The colleague directory behind every assign surface, and the client-side prediction
 * that decides which colleague gets an "Assign anyway?" confirm.
 *
 * Moved out of `features/inquiries/` when the work-order record surface needed the
 * SAME picker: see `./api.ts` for why one module beat two copies.
 *
 * ===========================================================================
 * THE PREDICTION IS A COURTESY. THE SERVER IS THE AUTHORITY.
 * ===========================================================================
 * `POST …/inquiries/bulk` answers `warnings:[{id, code}]` when the assignee it just wrote
 * is out of the row's country scope (owner rulings 1 and 3). That response is the fact.
 * What this module computes is a PREDICTION of it, made before the request, purely so the
 * operator is asked BEFORE the write rather than told after it — a confirm that arrives
 * once the assignment already exists is not a confirm, it is a receipt.
 *
 * The prediction is the same rule the API applies, run against the same two fields
 * (`is_group_admin`, `roles[].country`) that `GET /users` returns:
 *
 *     out of scope  ⟺  !is_group_admin && no role row for the current country
 *
 * It can nevertheless disagree with the server, and the design assumes it will:
 *
 *  · the directory is a page, capped at `DIRECTORY_PAGE`, and a stale cache is a cache;
 *  · a bulk selection can straddle countries — the confirm names the country the operator
 *    is LOOKING at, while the API judges each row against its OWN `country_id`;
 *  · roles change between the directory read and the write.
 *
 * So the two halves are deliberately belt and braces: this predicts and asks, and
 * `BulkResultNotice` reports whatever `warnings[]` actually came back, including warnings
 * this module never predicted. Correctness does not depend on the client agreeing with the
 * server — only the ORDER of the question does.
 */

import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { MIN_COLLEAGUE_SEARCH, fetchColleagues, fetchUserCard, userKeys } from './api';
import type { DirectoryUserDto, UserCardDto } from './api';
import type { CountryCode } from '../../domain/countries';

/** One row of the picker. */
export interface Colleague {
  /**
   * The id as `assigned_to` carries it — a STRING. `UserView.id` is serialised as a
   * number and `inquiries.assigned_to` is a `BIGINT` sent as text, so the conversion
   * happens once, here, rather than at each of the three places that compare it.
   */
  id: string;
  name: string;
  email: string;
  /** Countries this colleague holds a role in. Empty is a real state — see `no_scope`. */
  countries: readonly CountryCode[];
  /** The prediction. See the file header for what it is and is not. */
  outOfScope: boolean;
}

/**
 * The rule, as the API states it (`inquiries.service.ts`, owner ruling 1): an assignee is
 * out of scope for a country unless they are a group admin or hold a `user_country_roles`
 * row for it.
 *
 * The group-admin branch is FIRST and unconditional because `grp:true` bypasses country
 * scope entirely — a group admin with zero role rows can see every country, and marking
 * them "not in CD" because their `roles` array is empty would be a warning about a person
 * who is in fact the least restricted account in the system.
 */
export function isOutOfCountryScope(
  user: Pick<DirectoryUserDto, 'is_group_admin' | 'roles'>,
  countryCode: CountryCode,
): boolean {
  if (user.is_group_admin) return false;
  return !user.roles.some((entry) => entry.country === countryCode);
}

/**
 * Directory rows → picker rows.
 *
 * `excludeId` drops the caller. "Assign to me" is already the first item in the same menu,
 * and a second control with an identical effect reads as a different one — the operator
 * has to work out whether picking their own name does something "Assign to me" does not.
 *
 * Sorted by name, case-insensitively, with the id as the tiebreak so the order is stable
 * across renders rather than dependent on the server's `ORDER BY id`. In-scope people are
 * NOT floated to the top: the marker is what carries that information, and a reordering
 * that the operator cannot see the rule for is a menu whose items move for no visible
 * reason.
 */
export function toColleagues(
  users: readonly DirectoryUserDto[],
  countryCode: CountryCode,
  excludeId: string | null,
): Colleague[] {
  return users
    .map((user) => ({
      id: String(user.id),
      name: user.name,
      email: user.email,
      countries: user.roles.map((entry) => entry.country),
      outOfScope: isOutOfCountryScope(user, countryCode),
    }))
    .filter((colleague) => colleague.id !== excludeId)
    .sort((a, b) => {
      const byName = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      return byName !== 0 ? byName : a.id.localeCompare(b.id);
    });
}

/**
 * What the Assign menu renders instead of guessing.
 *
 * The three states are explicit rather than derived from `people.length` because they are
 * three different sentences: "still loading" is not "nobody", and "the directory could not
 * be read" is REALLY not "nobody" — a picker that silently shows an empty list on a failed
 * request tells the operator their colleagues do not exist.
 */
export interface ColleagueDirectory {
  /**
   * `idle` is the state before the operator has typed enough to search — a FOURTH
   * sentence, and the one this picker now opens in. Collapsing it into `ready`
   * with an empty list would tell the operator they have no colleagues when in
   * fact nobody has been asked yet.
   */
  status: 'idle' | 'loading' | 'error' | 'ready';
  people: readonly Colleague[];
  /** The server's total for THIS SEARCH, so a capped result can admit it is capped. */
  total: number;
  /**
   * The server had more matches than it returned.
   *
   * Derived from the response (`total > data.length`), NOT from comparing `people.length`
   * to {@link DIRECTORY_PAGE}: `people` has the caller removed from it, so that comparison
   * would announce a truncation on every complete result of exactly one page.
   */
  capped: boolean;
}

/**
 * 250ms — the same delay the list toolbars' `SearchField` settled on, so the two
 * search boxes in this app feel like one app.
 */
export const COLLEAGUE_SEARCH_DEBOUNCE_MS = 250;

/**
 * Hold a term still for {@link COLLEAGUE_SEARCH_DEBOUNCE_MS} before anyone acts on it.
 *
 * Re-authored here rather than imported from `features/quotes/use-quotes.ts`
 * (`useDebounced`) because that one is a generic this module does not want to depend
 * on — the same reason the admin users screen re-authored `SearchField`. It is
 * narrower than that generic: a string, so no `JSON.stringify` round-trip is needed
 * to compare it.
 *
 * The early return when `settled === value` is what stops the effect re-arming a
 * timer against the value it just applied.
 */
export function useDebouncedTerm(
  value: string,
  delayMs: number = COLLEAGUE_SEARCH_DEBOUNCE_MS,
): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    if (settled === value) return undefined;
    const timer = window.setTimeout(() => setSettled(value), delayMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, settled, delayMs]);
  return settled;
}

/**
 * One colleague by id — the answer to "who is on this card?".
 *
 * Gated on `crf.view` at the API (OQ-WO-ASSIGNEE-NAME, owner-ruled), which every
 * caller who can open a work order already holds — so unlike {@link useColleagues}
 * this needs no permission prop: if the caller could not read the record, they
 * are not looking at this screen.
 *
 * A long `staleTime`: a person's display name is the least volatile fact in the
 * system, and an assignee resolved once should not cost a request every time the
 * record refetches after a mutation.
 */
export function useUserCard(
  userId: string | null,
  enabled = true,
): UseQueryResult<UserCardDto> {
  return useQuery({
    queryKey: userKeys.card(userId ?? ''),
    queryFn: ({ signal }) => fetchUserCard(userId ?? '', signal),
    enabled: enabled && userId !== null && userId !== '',
    staleTime: 5 * 60_000,
  });
}

/**
 * The directory query, gated on `users.manage`.
 *
 * `null` — not merely an empty list — is the answer for a caller who does not hold it, and
 * the caller of this hook renders today's unchanged menu for that case (C5). The
 * distinction matters: `GET /users` is `@RequirePerm('users.manage')`, so firing it without
 * the permission is a guaranteed 403, and an empty picker where a 403 belongs is the kind
 * of "the feature is broken" report that costs a day to trace.
 *
 * `enabled` is threaded into `useQuery` rather than the hook being called conditionally —
 * hooks cannot be, and a Tech Support user must not spend a request on a route that will refuse
 * them.
 */
export function useColleagues(
  countryCode: CountryCode,
  enabled: boolean,
  excludeId: string | null,
  term: string,
): ColleagueDirectory | null {
  // Trimmed HERE, so the query key and the length gate both see the same value the
  // server will. Typing a trailing space must not spend a second request — the
  // party picker settled this (`usePartySearch`) and the reasoning is identical.
  const trimmed = term.trim();
  const searching = trimmed.length >= MIN_COLLEAGUE_SEARCH;

  const query = useQuery({
    queryKey: userKeys.search(trimmed),
    queryFn: ({ signal }) => fetchColleagues(trimmed, signal),
    enabled: enabled && searching,
    // A directory changes when somebody is hired, not between two keystrokes. One
    // minute of freshness means re-typing a term answers from cache, and is short
    // enough that a role granted a moment ago shows up without a reload.
    staleTime: 60_000,
    // The previous matches stay on screen while the next query is in flight, so the
    // list does not blink to "Searching…" on every keystroke. The party picker's
    // choice, for the same reason.
    placeholderData: keepPreviousData,
  });

  const people = useMemo(
    () => toColleagues(query.data?.data ?? [], countryCode, excludeId),
    [query.data, countryCode, excludeId],
  );

  if (!enabled) return null;
  // Nobody has been asked yet. NOT 'ready' with an empty list, which would read as
  // "you have no colleagues" (the honest-states rule this file already lived by —
  // there is simply one more state now that the picker searches).
  if (!searching) return { status: 'idle', people: [], total: 0, capped: false };
  if (query.isPending) return { status: 'loading', people: [], total: 0, capped: false };
  if (query.isError) return { status: 'error', people: [], total: 0, capped: false };
  const page = query.data;
  return {
    status: 'ready',
    people,
    total: page?.total ?? people.length,
    capped: page !== undefined && page.total > page.data.length,
  };
}
